import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

// Which org you are looking at is ONE hook, like the spoke and the session (ADR 0003 §4): never
// per-page state, never a query-string convention. The selection is 'mine' (the org your mailbox
// puts you in), 'all' (every org summarised), or an org key. It survives a reload in localStorage.
//
// The API client appends it to every GET, so it has to be readable WITHOUT React — hence the small
// module-level store below, with the context as a thin subscriber over it.
//
// Changing it re-reads the whole screen: setOrg() resets the TanStack Query cache. Reset, not
// invalidate — invalidating keeps the previous org's rows on screen under the new org's name while
// the refetch is in flight, and a number under the wrong heading is a false statement, not a
// transition. Resetting drops the data, so every list says "Loading…" and then says the truth.
// It is also the only mechanism, deliberately: putting the selection in each query key would leave
// any call site that forgot it serving another org's rows forever, and there are ~80 of them.

/** 'mine' · 'all' · an org key (a mail domain, lower-case). */
export type OrgSelection = string

export const MINE: OrgSelection = 'mine'
export const ALL: OrgSelection = 'all'

const KEY = 'scout.org'

/** The query key of the org roster itself: the one read a change of org does not invalidate. */
export const ORGS_KEY = 'orgs'

/**
 * localStorage, when there is a working one. Node exposes a `localStorage` object with no methods on
 * it unless it was started with a store file, so having the property is not enough to believe it.
 */
function store(): Storage | null {
  try {
    const ls = globalThis.localStorage as Storage | undefined
    return ls && typeof ls.getItem === 'function' && typeof ls.setItem === 'function' ? ls : null
  } catch {
    return null
  }
}

function read(): OrgSelection {
  const saved = store()?.getItem(KEY)
  return saved && saved.length > 0 ? saved : MINE
}

let current: OrgSelection = read()
const listeners = new Set<(selection: OrgSelection) => void>()

/** The selection, readable outside React. The API client calls this on every GET. */
export function orgSelection(): OrgSelection {
  return current
}

/** Set the selection and persist it. Prefer useOrg().setOrg — this does NOT touch the query cache. */
export function setOrgSelection(next: OrgSelection) {
  current = next && next.length > 0 ? next : MINE
  store()?.setItem(KEY, current)
  for (const listener of [...listeners]) listener(current)
}

/** For tests and the provider: hear about a change made anywhere. */
export function subscribeOrg(listener: (selection: OrgSelection) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

interface OrgState {
  /** what is selected: 'mine', 'all', or an org key */
  org: OrgSelection
  /** select an org and re-read every screen that is open */
  setOrg: (next: OrgSelection) => void
}

const OrgContext = createContext<OrgState | null>(null)

export function OrgProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [org, setOrgState] = useState<OrgSelection>(orgSelection())

  useEffect(() => subscribeOrg(setOrgState), [])

  const setOrg = useCallback(
    (next: OrgSelection) => {
      if (next === orgSelection()) return
      setOrgSelection(next)
      // Every list on screen was answered for the org we just left. The roster of orgs is the one
      // read that is not scoped — the switcher asks for it without an org — so it stays, and the
      // control you just pressed does not blink out from under you.
      void qc.resetQueries({ predicate: (query) => query.queryKey[0] !== ORGS_KEY })
    },
    [qc],
  )

  const value = useMemo(() => ({ org, setOrg }), [org, setOrg])
  return createElement(OrgContext.Provider, { value }, children)
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error('useOrg must be used inside OrgProvider')
  return ctx
}
